import { ApplicationInsights, type ITelemetryPlugin } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { createBrowserHistory } from "history";
import { appConfigService } from "@/services/appConfigService";

/**
 * Properties object for telemetry events
 */
type TelemetryProperties = Record<string, string | number | boolean>;

/**
 * Measurements object for telemetry events
 */
type TelemetryMeasurements = Record<string, number>;

/**
 * Application Insights Service for React
 * Handles telemetry initialization and tracking
 */

let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;
let initializationPromise: Promise<ApplicationInsights | null> | null = null;

/**
 * Gets the React plugin instance for use in React Context
 */
export function getReactPlugin(): ReactPlugin | null {
  return reactPlugin;
}

/**
 * Gets the Application Insights instance
 */
export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

/**
 * Initializes Application Insights with React plugin
 */
async function initializeApplicationInsights(): Promise<ApplicationInsights | null> {
  if (appInsights) {
    return appInsights;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async (): Promise<ApplicationInsights | null> => {
    try {
      const envData = await appConfigService.getAppEnvironmentData();
      
      if (!envData.applicationInsightsConnectionString) {
        console.warn("Application Insights connection string not configured - telemetry will not be collected");
        initializationPromise = null;
        return null;
      }

      // Create React plugin instance
      reactPlugin = new ReactPlugin();

      // Create browser history for enhanced SPA tracking
      const browserHistory = createBrowserHistory();

      // Initialize Application Insights
      const appInsightsInstance = new ApplicationInsights({
        config: {
          connectionString: envData.applicationInsightsConnectionString,
          extensions: [reactPlugin as unknown as ITelemetryPlugin],
          extensionConfig: {
            [reactPlugin.identifier]: { history: browserHistory },
          },
          enableAutoRouteTracking: true,
        },
      });

      appInsightsInstance.loadAppInsights();
      appInsights = appInsightsInstance;
      initializationPromise = null;
      
      return appInsightsInstance;
    } catch (error) {
      console.error("Failed to initialize Application Insights:", error);
      initializationPromise = null;
      throw error;
    }
  })();

  return initializationPromise;
}

/**
 * Tracks a custom event
 */
function trackEvent(name: string, properties?: TelemetryProperties, measurements?: TelemetryMeasurements): void {
  if (appInsights && appInsights.trackEvent) {
    appInsights.trackEvent({ name, properties, measurements });
  }
}

/**
 * Tracks a page view
 */
function trackPageView(name?: string, uri?: string, properties?: TelemetryProperties, measurements?: TelemetryMeasurements): void {
  if (appInsights && appInsights.trackPageView) {
    appInsights.trackPageView({ name, uri, properties, measurements });
  }
}

/**
 * Tracks an exception
 */
function trackException(exception: Error, severityLevel?: number, properties?: TelemetryProperties, measurements?: TelemetryMeasurements): void {
  if (appInsights && appInsights.trackException) {
    appInsights.trackException({ 
      exception, 
      severityLevel, 
      properties, 
      measurements 
    });
  }
}

/**
 * Tracks a custom metric
 */
function trackMetric(name: string, average: number, sampleCount?: number, min?: number, max?: number, properties?: TelemetryProperties): void {
  if (appInsights && appInsights.trackMetric) {
    appInsights.trackMetric({ 
      name, 
      average, 
      sampleCount, 
      min, 
      max, 
      properties 
    });
  }
}

/**
 * Flushes all pending telemetry
 */
function flush(): void {
  if (appInsights && appInsights.flush) {
    appInsights.flush();
  }
}

export const appInsightsService = {
  initializeApplicationInsights,
  trackEvent,
  trackPageView,
  trackException,
  trackMetric,
  flush,
  getReactPlugin,
  getAppInsights,
};